/**
 * File Utilities
 * Helper functions for file handling, sanitization, and validation
 */

/**
 * Sanitize filename to be safe for cloud storage (Supabase, S3, etc.)
 * Removes or replaces characters that are invalid in storage object keys
 *
 * @param {string} filename - Original filename to sanitize
 * @param {Object} options - Configuration options
 * @param {boolean} options.preserveExtension - Keep file extension intact (default: true)
 * @param {string} options.replacement - Character to replace invalid chars with (default: '_')
 * @returns {string} - Sanitized filename safe for storage
 *
 * @example
 * sanitizeFilename("Plan R'.jpg") // "Plan_R.jpg"
 * sanitizeFilename("Document (copy).pdf") // "Document_copy.pdf"
 * sanitizeFilename("<script>bad.exe</script>") // "script_bad.exe_script"
 */
export function sanitizeFilename(filename, options = {}) {
  const {
    preserveExtension = true,
    replacement = '_'
  } = options;

  if (!filename || typeof filename !== 'string') {
    return 'unnamed_file';
  }

  let name = filename.trim();

  // Handle empty or whitespace-only filenames
  if (name.length === 0) {
    return 'unnamed_file';
  }

  // Split into name and extension if preserving extension
  let baseName = name;
  let extension = '';

  if (preserveExtension) {
    const lastDotIndex = name.lastIndexOf('.');
    if (lastDotIndex > 0) { // Don't treat hidden files as extensions (.gitignore)
      baseName = name.substring(0, lastDotIndex);
      extension = name.substring(lastDotIndex); // Includes the dot
    }
  }

  // Remove/replace invalid characters for cloud storage
  // This includes:
  // - Windows forbidden: < > : " / \ | ? *
  // - Problematic for URLs/storage: ' (apostrophe/single quote), ~ (tilde from 8.3 filenames)
  // - Potentially problematic: ` (backtick), # (hash), % (percent), & (ampersand)
  // - Square brackets [] - invalid in Supabase storage keys
  // - Control characters and non-printable chars
  baseName = baseName
    .replace(/[<>:"/\\|?*'`#%&~[\]]/g, replacement) // Replace forbidden and problematic chars
    // eslint-disable-next-line no-control-regex
    .replace(/[\x00-\x1f\x7f-\x9f]/g, '') // Remove control characters
    .replace(/\s+/g, replacement) // Replace whitespace with replacement char
    .replace(new RegExp(`${replacement}{2,}`, 'g'), replacement) // Remove duplicate replacements
    .replace(/^[._-]+/, '') // Remove leading dots, underscores, dashes (hidden files, etc.)
    .replace(/[._-]+$/, ''); // Remove trailing dots, underscores, dashes

  // Sanitize extension too (but less aggressively)
  if (extension) {
    extension = extension
      .replace(/[<>:"/\\|?*'`#%&[\]]/g, '') // Remove forbidden chars from extension
      .replace(/\s+/g, '') // Remove spaces from extension
      .toLowerCase(); // Normalize extension to lowercase
  }

  // Handle edge case: if sanitization removed everything
  if (baseName.length === 0) {
    baseName = 'file';
  }

  return baseName + extension;
}

/**
 * Validate file extension against a list of allowed extensions
 *
 * @param {string} filename - Filename to validate
 * @param {string[]} allowedExtensions - Array of allowed extensions (e.g., ['.jpg', '.png'])
 * @returns {boolean} - True if extension is allowed
 *
 * @example
 * isAllowedExtension('photo.jpg', ['.jpg', '.png']) // true
 * isAllowedExtension('virus.exe', ['.jpg', '.png']) // false
 */
export function isAllowedExtension(filename, allowedExtensions) {
  if (!filename || !Array.isArray(allowedExtensions)) {
    return false;
  }

  const extension = filename.toLowerCase().substring(filename.lastIndexOf('.'));
  return allowedExtensions.some(ext => ext.toLowerCase() === extension);
}

/**
 * Get file extension from filename
 *
 * @param {string} filename - Filename to extract extension from
 * @returns {string} - File extension including dot (e.g., '.jpg') or empty string
 *
 * @example
 * getFileExtension('document.pdf') // '.pdf'
 * getFileExtension('archive.tar.gz') // '.gz'
 * getFileExtension('noextension') // ''
 */
export function getFileExtension(filename) {
  if (!filename || typeof filename !== 'string') {
    return '';
  }

  const lastDotIndex = filename.lastIndexOf('.');
  if (lastDotIndex > 0 && lastDotIndex < filename.length - 1) {
    return filename.substring(lastDotIndex).toLowerCase();
  }

  return '';
}

/**
 * Format file size in bytes to human-readable string
 *
 * @param {number} bytes - File size in bytes
 * @param {number} decimals - Number of decimal places (default: 2)
 * @returns {string} - Formatted size string (e.g., '1.5 MB')
 *
 * @example
 * formatFileSize(1536) // '1.50 KB'
 * formatFileSize(1048576) // '1.00 MB'
 * formatFileSize(0) // '0 Bytes'
 */
export function formatFileSize(bytes, decimals = 2) {
  if (bytes === 0) return '0 Bytes';
  if (!bytes || bytes < 0) return 'Unknown';

  const k = 1024;
  const dm = decimals < 0 ? 0 : decimals;
  const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];

  const i = Math.floor(Math.log(bytes) / Math.log(k));

  return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + ' ' + sizes[i];
}

/**
 * Check if filename is safe (doesn't contain obvious malicious patterns)
 * This is a basic check - server-side validation is still required
 *
 * @param {string} filename - Filename to check
 * @returns {Object} - {isSafe: boolean, reason?: string}
 *
 * @example
 * isSafeFilename('document.pdf') // {isSafe: true}
 * isSafeFilename('../../../etc/passwd') // {isSafe: false, reason: 'Path traversal detected'}
 */
export function isSafeFilename(filename) {
  if (!filename || typeof filename !== 'string') {
    return { isSafe: false, reason: 'Invalid filename' };
  }

  // Check for path traversal attempts
  if (filename.includes('..') || filename.includes('/') || filename.includes('\\')) {
    return { isSafe: false, reason: 'Path traversal detected' };
  }

  // Check for null bytes (common in exploit attempts)
  if (filename.includes('\0')) {
    return { isSafe: false, reason: 'Null byte detected' };
  }

  // Check for excessively long filenames (potential DoS)
  if (filename.length > 255) {
    return { isSafe: false, reason: 'Filename too long (max 255 characters)' };
  }

  return { isSafe: true };
}

/**
 * Rewrite a Supabase Storage object URL to the Cloudflare-proxied download
 * edge (cdn.ab-civil.com).
 *
 * Why: db.ab-civil.com is served straight from the German datacenter, and the
 * public Germany<->US transit (Zayo / Hurricane Electric) periodically loses
 * packets, which collapses single-stream TCP and drags 1GB+ downloads down to
 * <0.2 MB/s for US clients. cdn.ab-civil.com is the SAME storage backend but
 * proxied through Cloudflare, whose backbone bypasses that path (~40x faster in
 * testing). Only object read/render paths are exposed on cdn, and only GETs —
 * uploads still go direct to db.ab-civil.com (Cloudflare caps request bodies at
 * 100MB, which would break our large uploads).
 *
 * Applied at download time so it fixes both freshly-generated URLs and the many
 * db.ab-civil.com URLs already stored in the database. Any non-storage or
 * non-db URL is returned unchanged.
 *
 * @param {string} url - A file URL (may already be a cdn URL, or unrelated)
 * @returns {string} The URL, host-swapped to cdn when it's a db storage object
 */
export function toCdnDownloadUrl(url) {
  if (!url || typeof url !== 'string') return url;
  return url.replace(
    /^https:\/\/db\.ab-civil\.com\/storage\/v1\/object\//,
    'https://cdn.ab-civil.com/storage/v1/object/'
  );
}

/**
 * Force-download a file to disk, bypassing in-browser viewers such as the
 * Adobe Acrobat extension (which hijacks any PDF opened in a browser tab).
 *
 * Why fetch-as-blob instead of a plain `<a download>` or a `?download` param?
 *  - Our files live on db.ab-civil.com (cross-origin), so browsers IGNORE the
 *    `download` attribute and the link just opens in a tab.
 *  - Our self-hosted Supabase storage does not send a `Content-Disposition`
 *    header even with `?download`, so the server can't be asked to force it.
 * Storage CORS is open (`access-control-allow-origin: *`), so we fetch the file
 * as a blob and save it from an object URL — the same pattern ModelQuotesModal
 * already uses for its "Download all" zip. No tab is ever created for Adobe to
 * grab.
 *
 * @param {string} url - Public/signed file URL
 * @param {string} filename - Desired saved filename
 * @returns {Promise<void>} Resolves once the download has been triggered
 * @throws Re-throws on failure (after falling back to opening the URL) so
 *         callers can surface an error state
 */
export async function downloadFileToDisk(url, filename) {
  if (!url) {
    throw new Error('No file URL provided for download');
  }

  // Route the fetch through the Cloudflare download edge (no-op for non-db URLs).
  const downloadUrl = toCdnDownloadUrl(url);

  try {
    const response = await fetch(downloadUrl);
    if (!response.ok) {
      throw new Error(`HTTP ${response.status} ${response.statusText}`);
    }

    const blob = await response.blob();
    const objectUrl = URL.createObjectURL(blob);

    const link = document.createElement('a');
    link.href = objectUrl;
    link.download = filename || 'download';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    // Give the browser a moment to start the download before releasing the blob
    setTimeout(() => URL.revokeObjectURL(objectUrl), 1000);
  } catch (error) {
    // Last resort: open the file so it's still reachable, then surface the error
    console.error('Direct download failed, opening in new tab instead:', error);
    window.open(downloadUrl, '_blank', 'noopener,noreferrer');
    throw error;
  }
}

const fileUtils = {
  sanitizeFilename,
  isAllowedExtension,
  getFileExtension,
  formatFileSize,
  isSafeFilename,
  downloadFileToDisk,
  toCdnDownloadUrl
};

export default fileUtils;
